class BestSellersSlider extends HTMLElement {
  constructor() {
    super();
    this.list = this.querySelector('.best-sellers__list');
    this.progressFill = this.querySelector('.best-sellers__progress-fill');

    if (!this.list || !this.progressFill) return;

    this.onScroll = this.updateProgress.bind(this);
    this.list.addEventListener('scroll', this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(this.onScroll);
    this.resizeObserver.observe(this.list);

    this.updateProgress();
  }

  updateProgress() {
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;

    if (maxScroll <= 0) {
      this.progressFill.style.width = '100%';
      return;
    }

    const progress = ((this.list.scrollLeft + this.list.clientWidth) / this.list.scrollWidth) * 100;
    this.progressFill.style.width = `${Math.min(100, Math.max(0, progress))}%`;
  }

  disconnectedCallback() {
    this.list?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }
}

class BestSellersCard extends HTMLElement {
  connectedCallback() {
    this.swatches = this.querySelectorAll('.best-sellers-card__swatch');
    this.link = this.querySelector('.best-sellers-card__link');
    this.mediaImages = this.querySelector('.best-sellers-card__media-images');
    this.primaryImage = this.querySelector('.best-sellers-card__image--primary');
    this.secondaryImage = this.querySelector('.best-sellers-card__image--secondary');
    this.titleLink = this.querySelector('.best-sellers-card__title');
    this.priceContainer = this.querySelector('.best-sellers-card__price');

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.selectSwatch(swatch);
      });
    });
  }

  updatePrimaryImage(url, srcset) {
    if (!url || !this.primaryImage) {
      return;
    }

    this.primaryImage.src = url;

    if (srcset) {
      this.primaryImage.srcset = srcset;
    } else {
      this.primaryImage.removeAttribute('srcset');
    }
  }

  updateSecondaryImage(url, srcset) {
    if (!url) {
      if (this.secondaryImage) {
        this.secondaryImage.remove();
        this.secondaryImage = null;
      }
      return;
    }

    if (!this.secondaryImage) {
      this.secondaryImage = document.createElement('img');
      this.secondaryImage.className = 'best-sellers-card__image best-sellers-card__image--secondary';
      this.secondaryImage.loading = 'lazy';
      this.secondaryImage.decoding = 'async';
      this.secondaryImage.sizes =
        this.primaryImage?.sizes ||
        '(min-width: 750px) calc((min(100vw, var(--page-width)) - 4.8rem - (var(--bs-card-gap, 0.8rem) * 3)) / 4), calc(100vw - 4.8rem)';
      this.mediaImages?.appendChild(this.secondaryImage);
    }

    this.secondaryImage.src = url;

    if (srcset) {
      this.secondaryImage.srcset = srcset;
    } else {
      this.secondaryImage.removeAttribute('srcset');
    }
  }

  selectSwatch(swatch) {
    this.swatches.forEach((item) => item.setAttribute('aria-pressed', 'false'));
    swatch.setAttribute('aria-pressed', 'true');

    if (swatch.dataset.variantUrl) {
      if (this.link) this.link.href = swatch.dataset.variantUrl;
      if (this.titleLink) this.titleLink.href = swatch.dataset.variantUrl;
    }

    const hasColorMedia = Boolean(swatch.dataset.colorPrimaryUrl);
    const primaryUrl = swatch.dataset.colorPrimaryUrl || swatch.dataset.imageUrl;
    const primarySrcset = swatch.dataset.colorPrimarySrcset || swatch.dataset.imageSrcset;

    this.updatePrimaryImage(primaryUrl, primarySrcset?.trim());

    if (hasColorMedia) {
      if (swatch.dataset.colorSecondaryUrl) {
        this.updateSecondaryImage(
          swatch.dataset.colorSecondaryUrl,
          swatch.dataset.colorSecondarySrcset?.trim()
        );
      } else {
        this.updateSecondaryImage(null);
      }
    }

    if (this.priceContainer && swatch.dataset.price) {
      const comparePrice = swatch.dataset.comparePrice;

      if (comparePrice) {
        this.priceContainer.innerHTML = `
          <span class="best-sellers-card__price-sale">${swatch.dataset.price}</span>
          <span class="best-sellers-card__price-compare"><s>${comparePrice}</s></span>
        `;
      } else {
        this.priceContainer.innerHTML = `
          <span class="best-sellers-card__price-sale">${swatch.dataset.price}</span>
        `;
      }
    }
  }
}

if (!customElements.get('best-sellers-slider')) {
  customElements.define('best-sellers-slider', BestSellersSlider);
}

if (!customElements.get('best-sellers-card')) {
  customElements.define('best-sellers-card', BestSellersCard);
}
